<script setup lang="ts">
import { useAppStore } from "@/store/app";
import { ChevronDownIcon, DotsGridIcon, PlusIcon } from "@gohighlevel/ghl-icons/24/outline";
import {
  UIButton,
  UICollapse,
  UICollapseItem,
  UIInput,
  UIInputGroup,
  UISelect,
} from "@gohighlevel/ghl-ui";
import { truncate } from "lodash";
import { storeToRefs } from "pinia";
import { computed, onMounted, ref, watch } from "vue";
import { VueDraggableNext } from "vue-draggable-next";
import { useI18n } from "vue-i18n";

const store = useAppStore();

const { t } = useI18n();
const { getCustomFieldsFromStore, getCustomFieldFoldersFromStore, getCustomObjectsFromStore } =
  storeToRefs(store);
const searchTerm = ref("");

// Dynamic options that will be populated from store
const customFieldsOptions = ref([]);
const customFieldsType = ref("contact");

// Exclude specific standard opportunity fields by fieldKey
const EXCLUDED_OPPORTUNITY_FIELD_KEYS = new Set([
  "opportunity.pipeline_id",
  "opportunity.pipeline_stage_id",
  "opportunity.status",
  "opportunity.monetary_value",
]);

/**
 * Update dropdown options from store data
 * Sort objects by priority: contact, opportunity, business, then remaining
 */
const updateDropdownOptions = () => {
  const objectsRaw = getCustomObjectsFromStore.value || [];
  // If custom objects feature is disabled, allow only Contact and Opportunity
  const objects = store.isCustomObjectEnabled
    ? objectsRaw
    : objectsRaw.filter((o: any) => o?.key === "contact" || o?.key === "opportunity");
  const priorityOrder = ["contact", "opportunity", "business"];

  // Sort objects by priority index (non-priority items get high index)
  const sortedObjects = [...objects].sort((a, b) => {
    const getPriority = (key) =>
      priorityOrder.indexOf(key) !== -1 ? priorityOrder.indexOf(key) : 999;
    return getPriority(a.key) - getPriority(b.key);
  });

  customFieldsOptions.value = sortedObjects.map((obj) => ({
    label: obj.labels.singular,
    value: obj.key,
  }));
};

const filterByType = (fields) =>
  fields.filter((customField) => customField.model === customFieldsType.value);
const customFieldsCollection = ref(filterByType(getCustomFieldsFromStore.value));
const customFoldersCollection = ref(filterByType(getCustomFieldFoldersFromStore.value));
const groupedCustomFields = ref([]);

const openCustomFieldModal = () => {
  store.openCustomFieldsModel();
};

const updateCustomFieldsOptions = () => {
  customFieldsCollection.value = filterByType(getCustomFieldsFromStore.value);
  customFoldersCollection.value = filterByType(getCustomFieldFoldersFromStore.value);

  groupedCustomFields.value = customFoldersCollection.value
    .map((folder) => ({
      name: folder.name,
      fields: customFieldsCollection.value
        .filter((field) => field.parentId === folder._id)
        .filter((field) => {
          if (field?.model === "opportunity") {
            // Remove all Opportunity standard fields from the left sidebar
            if (isStandardField(field)) {
              return false;
            }
            return !EXCLUDED_OPPORTUNITY_FIELD_KEYS.has(field?.fieldKey);
          }
          return true;
        }),
      showCount: 10,
    }))
    .map((group) => ({
      ...group,
      fields: group.fields.filter((field) =>
        field.name.toLowerCase().includes(searchTerm.value.toLowerCase())
      ),
    }))
    .filter((group) => group.fields.length > 0);
};
const showMore = (group) => {
  const index = groupedCustomFields.value.indexOf(group);
  if (index !== -1) {
    groupedCustomFields.value[index].showCount += 10;
  }
};

// Disable dragging for standard fields that are already available in forms
const isStandardField = (field: any): boolean => {
  if (!field) {
    return false;
  }
  return (
    field?.dataType === "STANDARD_FIELD" ||
    field?.type === "standard_field" ||
    field?.standard === true
  );
};

// Gather current builder elements (form elements or slide elements for survey/quiz)
const currentBuilderElements = computed(() => {
  if (store.formId) {
    return store.elements || [];
  }
  // For survey/quiz, flatten slideData arrays
  const slides = store.slides || [];
  return slides.flatMap((s: any) => s?.slideData || []);
});

const isFieldAlreadyInBuilder = (field: any): boolean => {
  if (!field) {
    return false;
  }
  // Compare by id/Id/_id first (custom fields uniqueness)
  const candidateId = field?.id || field?.Id || field?._id;
  if (candidateId) {
    const existsById = currentBuilderElements.value?.some((el: any) => {
      const existingId = el?.id || el?.Id || el?._id;
      return existingId && existingId === candidateId;
    });
    if (existsById) {
      return true;
    }
  }

  // Fallback: compare by model + tag/fieldKey for standard/company/contact parity
  const model = field?.model;
  const queryKey = field?.hiddenFieldQueryKey;
  const fieldKey = field?.fieldKey;

  return currentBuilderElements.value?.some((el: any) => {
    const sameModel = (el?.model || "contact") === model;
    if (!sameModel) {
      return false;
    }
    const matchesByTag = queryKey && el?.tag === queryKey;
    const matchesByFieldKey = fieldKey && el?.fieldKey === fieldKey;
    return Boolean(matchesByTag || matchesByFieldKey);
  });
};

const shouldDisable = (field: any): boolean => {
  // Disable contact address child fields and phone if group_address is present in builder
  const groupAddressPresent = currentBuilderElements.value?.some(
    (el: any) => el?.tag === "group_address"
  );
  if (groupAddressPresent) {
    const addrKeys = new Set([
      "address",
      "street_address",
      "city",
      "state",
      "country",
      "postal_code",
      "phone",
    ]);
    const keyCandidateRaw = (field?.hiddenFieldQueryKey ||
      (typeof field?.fieldKey === "string" ? field.fieldKey.split(".").pop() : "") ||
      (typeof field?.name === "string" ? field.name : "")) as string;
    const keyCandidate = keyCandidateRaw.toString().trim().toLowerCase().replace(/\s+/g, "_");
    if (field?.model === "contact" && addrKeys.has(keyCandidate)) {
      return true;
    }
  }

  // Disable if the field already exists in the builder (by id/tag/fieldKey)
  if (isFieldAlreadyInBuilder(field)) {
    return true;
  }
  return false;
};

const allowMove = (evt: any): boolean => {
  const item = evt?.draggedContext?.element;
  return !shouldDisable(item);
};

// Initialize on component mount
onMounted(() => {
  updateDropdownOptions();
});

updateCustomFieldsOptions();

watch(
  () => store.isCustomObjectEnabled,
  () => {
    updateDropdownOptions();
  }
);

watch(
  () => store.customFields,
  () => {
    updateCustomFieldsOptions();
  }
);
</script>

<template>
  <div class="custom-list-container px-2 pb-3">
    <div id="add-custom-fields-btn">
      <div class="mb-1 flex items-center gap-x-4 p-1">
        <UISelect
          id="custom-field-type"
          v-model:value="customFieldsType"
          :options="customFieldsOptions"
          @update:value="updateCustomFieldsOptions"
        />
        <UIButton id="add-custom-field" type="tertiary" @click="openCustomFieldModal">
          <PlusIcon class="mr-2 h-4 w-4" /> {{ t("common.add") }}
        </UIButton>
      </div>
      <UIInputGroup class="p-1">
        <UIInput
          id="search-custom-field"
          v-model="searchTerm"
          type="text"
          :placeholder="t('common.customFieldSearch')"
          :clearable="true"
          @update:model-value="updateCustomFieldsOptions"
        />
      </UIInputGroup>
    </div>
    <div class="mt-28">
      <UICollapse :accordion="true" :arrow-placement="'left'">
        <UICollapseItem
          v-for="group in groupedCustomFields"
          :id="group.name"
          :key="group.name"
          :title="`${group.name} (${group.fields.length})`"
          :name="group.name"
        >
          <VueDraggableNext
            :list="group.fields.slice(0, group.showCount)"
            :sort="false"
            ghost-class="sortable-ghost"
            :group="{ name: 'elements', pull: 'clone', put: false }"
            :move="allowMove"
          >
            <div
              v-for="(item, index) in group.fields.slice(0, group.showCount)"
              :key="index"
              class="p-1"
            >
              <div
                class="custom-item"
                :class="{ 'is-disabled': shouldDisable(item) }"
                :aria-disabled="shouldDisable(item) ? 'true' : 'false'"
              >
                <span>{{ truncate(item.name, { length: 30 }) }}</span>
                <div class="icon-container">
                  <DotsGridIcon class="h-4 w-4" />
                </div>
              </div>
            </div>
          </VueDraggableNext>
          <div class="flex justify-center">
            <UIButton
              v-if="group.fields.length > group.showCount"
              id="show-more"
              text
              class="mt-2.5 text-sm"
              @click="showMore(group)"
            >
              {{ t("common.more") }} {{ group.fields.length - group.showCount }}
              <ChevronDownIcon class="ml-1 h-4 w-4" />
            </UIButton>
          </div>
        </UICollapseItem>
      </UICollapse>
    </div>
  </div>
</template>

<style scoped>
.custom-list-container {
  height: 81vh;
  overflow-y: auto;
  padding-bottom: v-bind("store.isNewBuilderLayoutEnabled ? '100px' : '0'");
}

.custom-item {
  background-color: white;
  padding: 10px 15px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  font-weight: 600;
  cursor: all-scroll;
  border: 1px solid #d0d5dd;
  -webkit-user-select: none;
  /* Safari */
  -ms-user-select: none;
  /* IE 10 and IE 11 */
  user-select: none;
  border-radius: 5px;
}

.custom-item:hover {
  box-shadow: 0 4px 8px -2px #1018281a, 0 2px 4px -2px #1018280f;
}

.custom-item.is-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.icon-container {
  width: 15px;
  height: 15px;
  margin: auto 0;
  padding: 0;
}

#custom-fields {
  display: flex;
  justify-content: center;
}

#custom-preview {
  max-width: 300px;
  width: 100%;
  background-color: #e7f3fe;
  height: 80vh;
  right: 0;
  display: flex;
  align-items: center;
}

#custom-elments {
  background-color: #e7f3fe;
  height: 80vh;
}

#add-custom-fields-btn {
  position: absolute;
  top: 85px;
  width: 92%;
  z-index: 9;
  background-color: #fff;
}
</style>
<style>
.n-collapse-item__header-main {
  color: #667085 !important;
  text-transform: uppercase;
  font-size: 12px;
  font-weight: 600 !important;
}
</style>
